import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../context/AuthContext";

const STATUS = {
  pending_payment: {
    label: "Pending payment",
    cls: "text-bronze border-bronze/40",
  },
  paid: { label: "Paid", cls: "text-emerald-700 border-emerald-700/40" },
  shipped: { label: "Shipped", cls: "text-ink border-ink/40" },
  cancelled: { label: "Cancelled", cls: "text-oxblood border-oxblood/40" },
};
const NEXT = {
  pending_payment: ["paid", "cancelled"],
  paid: ["shipped", "cancelled"],
  shipped: [],
  cancelled: ["pending_payment"],
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export const OrdersPanel = () => {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/admin/orders").then(({ data }) => setOrders(data));
  }, []);

  const setStatus = async (o, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${o.id}/status`, {
        status,
      });
      setOrders((os) => os.map((x) => (x.id === data.id ? data : x)));
      toast.success(
        `Order ${o.reference} marked ${STATUS[status].label.toLowerCase()}`,
      );
    } catch {
      toast.error("Could not update order");
    }
  };

  const pending = orders.filter((o) => o.status === "pending_payment").length;

  const viewLogo = async (o) => {
    // Open the tab synchronously (inside the click) so popup blockers allow it
    const win = window.open("", "_blank");
    try {
      const { data } = await api.get(`/admin/orders/${o.id}/logo`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(data);
      if (win) win.location = blobUrl;
      else window.location.href = blobUrl;
    } catch {
      win?.close();
      toast.error("Could not open the logo file");
    }
  };

  return (
    <section data-testid="admin-orders" className="space-y-4">
      <p
        data-testid="admin-orders-summary"
        className="font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink"
      >
        {orders.length} orders · {pending} awaiting payment
      </p>
      {orders.length === 0 && (
        <p data-testid="admin-orders-empty" className="text-sm text-mutedink">
          No orders yet.
        </p>
      )}
      {orders.map((o) => (
        <article
          key={o.id}
          data-testid={`order-${o.reference}`}
          className="bg-white border border-ink/10 p-5 grid gap-4 sm:grid-cols-[1fr_auto]"
        >
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-mono text-xs text-oxblood">
                {o.reference}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mutedink">
                {fmtDate(o.created_at)} · {o.brand}
              </span>
              <span
                data-testid={`order-status-${o.reference}`}
                className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${STATUS[o.status].cls}`}
              >
                {STATUS[o.status].label}
              </span>
            </div>
            <p className="mt-2 font-serif text-lg text-ink">
              {o.quantity} × {o.product_name} · {o.colour} · Size {o.size}
              <span className="font-mono text-sm text-oxblood ml-3">
                ${o.total} {o.currency}
              </span>
            </p>
            <p className="mt-1 text-sm text-mutedink">
              {o.customer.name} · {o.customer.email}
              {o.customer.phone ? ` · ${o.customer.phone}` : ""}
            </p>
            <p className="text-sm text-mutedink">
              {o.shipping.address_line1}
              {o.shipping.address_line2
                ? `, ${o.shipping.address_line2}`
                : ""}, {o.shipping.city}, {o.shipping.province}{" "}
              {o.shipping.postal_code}, {o.shipping.country}
            </p>
            {o.school_logo && (
              <p
                data-testid={`order-logo-${o.reference}`}
                className="mt-2 inline-flex items-center gap-2 border border-oxblood/30 bg-parchment px-3 py-1.5 text-xs text-ink"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-oxblood">
                  School logo
                </span>
                {o.school_name}
                <button
                  type="button"
                  data-testid={`order-logo-view-${o.reference}`}
                  onClick={() => viewLogo(o)}
                  className="underline underline-offset-2 hover:text-oxblood"
                >
                  View file
                </button>
                {o.liability_accepted && (
                  <span className="text-mutedink">· disclaimer accepted</span>
                )}
              </p>
            )}
          </div>
          <div className="flex sm:flex-col gap-2 sm:items-end">
            {NEXT[o.status].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`order-${o.reference}-mark-${s}`}
                onClick={() => setStatus(o, s)}
                className="font-mono text-[10px] uppercase tracking-[0.2em] border border-ink px-3 py-2 hover:bg-ink hover:text-parchment transition-colors whitespace-nowrap"
              >
                {s === "pending_payment" ? "Reopen" : `Mark ${STATUS[s].label}`}
              </button>
            ))}
          </div>
        </article>
      ))}
    </section>
  );
};
